import { Box3, Mesh, Object3D, Quaternion, Vector3 } from "three";
import { OBB } from "three/examples/jsm/math/OBB.js";
import { forEachAsync } from "../util/Collection";
import {
  isObbDebugDrawEnabled,
  notifySugarModelBoundsChanged,
  volumeDebugColor,
} from "../util/DebugFlags";
import { disposeObbDebugGroup } from "../debug/dragCollisionDebugDraw";
import { createVolumeDebugWire } from "../debug/volumeDebugWire";
import {
  decomposeIntoOBBs,
  disposeObject3DDeep,
  transformObb,
} from "../util/GeometryUtil";
import { logger } from "../util/Logger";
import { LoaderManager } from "./Loader";
import { Sugar3DLoader } from "./Sugar3DLoader";
import { SugarModelJoint } from "./SugarModelJoint";
import {
  applyDefaultFeatureVisual,
  createDefaultFeatureVisual,
  createMaterial,
  createPart,
  ensureProductMaterialsCached,
  shouldLoadDefaultFeatureVisual,
  type DefaultFeatureVisual,
  SugarMaterial,
  SugarModelBase,
  SugarPart,
  SugarPartGroup,
} from "./SugarModelLoader";
import type { MaterialData, ModelData } from "../api/types/Product";
import type { HistoryDir, SugarStateSlice } from "../history/SugarObject";
import { trackStateRoot, untrackStateRoot } from "../history/SugarObject";
import { planeColliderPivotLiftM } from "./colliderType";

export interface SugarAsset {
  get data(): any;
  get id(): any;
  get name(): string;
  get code(): string;
  get thumbnailFileUrl(): string;
  get loaded(): boolean;
}
export class SugarModel extends SugarModelBase implements SugarAsset {
  parts?: SugarPart[];
  partMaterialGroups?: SugarPartGroup[];
  materials?: SugarMaterial[];
  private _joints?: SugarModelJoint[];

  //protected _root: Object3D = new Object3D();
  private _model: Object3D | undefined;
  private _data!: ModelData;
  protected _bound?: Box3;
  private _bounds: Mesh[] = [];
  private _loaded = false;
  private _defaultFeatureVisual: DefaultFeatureVisual | null = null;

  constructor(data: ModelData) {
    super();
    this._data = data;
    this.name =
      data.name || (data.id != null ? String(data.id) : "") || "SugarModel";
    trackStateRoot(this);
  }

  get data(): ModelData {
    return this._data;
  }
  /*get id(): string {
     return this._data.id;
   }
   get name(): string {
     return this._data.name;
   }*/
  get code(): string {
    return String(this._data.id);
  }

  get thumbnailFileUrl(): string {
    return this._data.thumbnailFileUrl ?? "";
  }

  get model(): Object3D | undefined {
    return this._model;
  }

  get bound() {
    return this._bound;
  }
  get bounds() {
    return this._bounds;
  }

  get joints() {
    return this._joints;
  }

  set joints(value) {
    this._joints = value;
  }

  protected _localObb = new OBB();
  private _worldObb = new OBB();
  protected _localObbs: OBB[] = [];
  private _obbDebugGroup: Object3D | null = null;

  private _clearObbDebugIfAny() {
    if (this._obbDebugGroup) {
      disposeObbDebugGroup(this._obbDebugGroup);
      this._obbDebugGroup = null;
    }
  }

  /**
   * Yeni glTF gelene kadar çarpışma/obb listesi eski mesh’e bağlı kalmasın (`loaded` hâlâ true olabilir).
   */
  private _invalidateCollisionBoundsForReload(): void {
    this._clearObbDebugIfAny();
    this._localObbs = [];
    this._localObb = new OBB();
    this._bound = undefined;
  }

  /** Room designer `load({ decomposeCollision: true })` ile açar. Varsayılan tek OBB. */
  private _decomposeCollision = false;

  /**
   * Geniş OBB = geometry AABB.
   * `decomposeCollision` açıksa XZ doluluk ızgarası L gibi formları 2–3 OBB’ye böler.
   * Yapısal mesh her zaman tek kutu (ince kanat çarpışmayı bozar).
   */
  private _applyLocalBounds(box: Box3): Box3 {
    if (!this._model) return box;
    this._model.userData.bound = box;
    this._bound = box;
    this._bounds = [];
    const size = new Vector3();
    box.getSize(size);
    const obb = new OBB();
    box.getCenter(obb.center);
    obb.halfSize.copy(size.multiplyScalar(0.5));
    this._localObb = obb;
    if (this._data?.structuralKind || !this._decomposeCollision) {
      this._localObbs = [this._localObb.clone()];
    } else {
      this._model.updateMatrixWorld(true);
      this._localObbs = decomposeIntoOBBs(this._model, box);
    }
    return box;
  }

  /**
   * AABB → taban merkezi (0,0,0): XZ orta, minY = 0.
   * Geometry yazılmaz (cache paylaşımlı); `_model.position` kayar.
   * Local OBB SugarModel uzayına çekilir — `this.matrixWorld` ile çarpışır.
   */
  private _applyVertexFloorPivot(box: Box3): void {
    if (!this._model || this._data?.structuralKind) return;
    if (!Number.isFinite(box.min.x) || box.isEmpty()) return;

    const shiftX = -(box.min.x + box.max.x) * 0.5;
    const shiftY = -box.min.y + planeColliderPivotLiftM(this._data);
    const shiftZ = -(box.min.z + box.max.z) * 0.5;
    this._model.position.set(shiftX, shiftY, shiftZ);
    if (shiftX === 0 && shiftY === 0 && shiftZ === 0) return;

    const shift = new Vector3(shiftX, shiftY, shiftZ);
    box.translate(shift);
    this._localObb.center.add(shift);
    for (const obb of this._localObbs) {
      if (obb === this._localObb) continue;
      obb.center.add(shift);
    }
  }

  /** Ayar veya `?debugObb=1` açılınca sahnedeki modeller bunu yeniden çağırır. */
  syncObbDebugVisualization(): void {
    this._syncObbDebugVisualization();
  }

  private _syncObbDebugVisualization() {
    this._clearObbDebugIfAny();
    if (!this._model || !isObbDebugDrawEnabled()) return;

    const group = new Object3D();
    group.name = "obbDebugGroup";
    const parts = volumeDebugColor("parts");
    const hull = volumeDebugColor("hull");
    for (const lo of this._localObbs) {
      group.add(createVolumeDebugWire(lo, parts));
    }
    group.add(createVolumeDebugWire(this._localObb, hull));
    // Floor-pivoted OBB is in SugarModel/root space; structural stays on `_model`.
    if (this._data?.structuralKind) this._model.add(group);
    else this.root.add(group);
    this._obbDebugGroup = group;
  }

  get localObb() {
    const localObb = this._localObb.clone();
    (localObb as any).model = this;
    return localObb;
  }

  get obb() {
    const w = transformObb(this._localObb, this.matrixWorld);
    this._worldObb.center.copy(w.center);
    this._worldObb.halfSize.copy(w.halfSize);
    this._worldObb.rotation.copy(w.rotation);
    (this._worldObb as any).model = this;
    return this._worldObb;
  }

  get localObbs(): OBB[] {
    return this._localObbs.map((o) => {
      const c = o.clone();
      (c as any).model = this;
      return c;
    });
  }

  get obbs(): OBB[] {
    return this._localObbs.map((o) => {
      const c = transformObb(o, this.matrixWorld);
      (c as any).model = this;
      return c;
    });
  }

  getCollisionLocalObb(): OBB {
    return this.localObb;
  }

  getCollisionLocalObbs(): OBB[] {
    return this.localObbs;
  }

  getCollisionWorldObbs(): OBB[] {
    return this.getCollisionLocalObbs().map((obb) => {
      const transformedObb = transformObb(obb, this.matrixWorld);
      (transformedObb as any).model = (obb as any).model ?? this;
      if ((obb as any).sourceModel)
        (transformedObb as any).sourceModel = (obb as any).sourceModel;
      if ((obb as any).configurator)
        (transformedObb as any).configurator = (obb as any).configurator;
      return transformedObb;
    });
  }

  set model(value: Object3D) {
    if (this._model === value && this._bound) {
      return;
    }

    if (this._model && this._model != value) {
      this._clearObbDebugIfAny();
      this._model?.removeFromParent();
      (this._model as any).model = undefined;
    }

    this._model = value;
    if (!this.model) {
      logger.warn("Model is undefined!!!");
      return;
    }

    const parent = this._model?.parent;
    parent?.remove(this._model);

    const originPosition = this._model.position.clone();
    const originScale = this._model.scale.clone();
    const originRotation = this._model.quaternion.clone();
    this._model.position.set(0, 0, 0);
    this._model.scale.set(1, 1, 1);
    this._model.quaternion.copy(new Quaternion());

    (this._model as any).model = this;
    const box = this._applyLocalBounds(
      new Box3().setFromObject(this._model, false),
    );

    this.root.add(this._model);
    this._model.updateMatrixWorld(true);

    parent?.add(this._model);
    this._model.scale.copy(originScale);
    this._model.quaternion.copy(originRotation);
    if (this._data?.structuralKind) {
      this._model.position.copy(originPosition);
    } else {
      this._applyVertexFloorPivot(box);
    }
    this._model.updateMatrixWorld(true);

    this._syncObbDebugVisualization();
    notifySugarModelBoundsChanged();
  }

  recomputeBound() {
    if (!this._model) return;
    const originPosition = this._model.position.clone();
    const originScale = this._model.scale.clone();
    const originRotation = this._model.quaternion.clone();
    const parent = this._model.parent;
    parent?.remove(this._model);
    this._model.position.set(0, 0, 0);
    this._model.scale.set(1, 1, 1);
    this._model.quaternion.copy(new Quaternion());
    this._model.updateMatrixWorld(true);

    const box = this._applyLocalBounds(
      new Box3().setFromObject(this._model, false),
    );

    if (parent) parent.add(this._model);
    this._model.scale.copy(originScale);
    this._model.quaternion.copy(originRotation);
    if (this._data?.structuralKind) {
      this._model.position.copy(originPosition);
    } else {
      this._applyVertexFloorPivot(box);
    }
    this._model.updateMatrixWorld(true);

    this._syncObbDebugVisualization();
    notifySugarModelBoundsChanged();
  }

  get loaded() {
    return this._loaded;
  }

  set loaded(value: boolean) {
    this._loaded = value;
  }

  /** Undo/export document slice — transform + part materials. */
  get state(): SugarStateSlice {
    return {
      kind: "sugarModel",
      value: {
        surfaceAnchors: structuredClone(
          Array.isArray(this.userData.surfaceAnchors)
            ? this.userData.surfaceAnchors
            : this.userData.surfaceAnchor
              ? [this.userData.surfaceAnchor]
              : [],
        ),
        partMaterials:
          this.partMaterialGroups?.map((g) => ({
            code: g.code,
            materialId: g.material?.id ?? "",
          })) ?? [],
        transform: {
          p: [this.position.x, this.position.y, this.position.z],
          q: [
            this.quaternion.x,
            this.quaternion.y,
            this.quaternion.z,
            this.quaternion.w,
          ],
        },
      },
    };
  }

  applyState(slice: SugarStateSlice, _dir: HistoryDir): void {
    if (slice.kind !== "sugarModel") return;
    const value = slice.value as {
      surfaceAnchors?: unknown[];
      surfaceAnchor?: unknown;
      partMaterials: Array<{ code: string; materialId: string }>;
      transform: {
        p: [number, number, number];
        q: [number, number, number, number];
      };
    };
    const anchors = Array.isArray(value.surfaceAnchors)
      ? value.surfaceAnchors
      : value.surfaceAnchor
        ? [value.surfaceAnchor]
        : [];
    if (anchors.length) {
      this.userData.surfaceAnchors = structuredClone(anchors);
    } else {
      delete this.userData.surfaceAnchors;
    }
    delete this.userData.surfaceAnchor;
    this.position.set(
      value.transform.p[0],
      value.transform.p[1],
      value.transform.p[2],
    );
    this.quaternion.set(
      value.transform.q[0],
      value.transform.q[1],
      value.transform.q[2],
      value.transform.q[3],
    );
    this.updateMatrixWorld(true);
    for (const { code, materialId } of value.partMaterials) {
      const group = this.partMaterialGroups?.find((g) => g.code === code);
      if (!group) continue;
      const mat = group.materials?.find((m) => m.id === materialId);
      if (mat) {
        group.material = mat;
        group.apply(this);
      }
    }
  }

  async _loadModel(
    sugarModel: SugarModel,
    loadPromises: Promise<any[]>,
    onAfterLoad?: (
      sugarModel: SugarModel,
      results: any[],
    ) => void | Promise<void>,
    onLoadStageProgress?: (progress: number) => void,
  ) {
    const oldModelToRemove = this.model;
    const results = await loadPromises;
    onLoadStageProgress?.(0.15);

    this.model = results[0];
    onLoadStageProgress?.(0.35);

    for (const part of sugarModel.parts ?? []) {
      await part.apply(sugarModel);
    }
    onLoadStageProgress?.(0.55);

    for (const group of sugarModel.partMaterialGroups ?? []) {
      await group.apply(sugarModel);
    }
    if (sugarModel._defaultFeatureVisual) {
      await applyDefaultFeatureVisual(
        sugarModel,
        sugarModel._defaultFeatureVisual,
      );
      sugarModel._defaultFeatureVisual = null;
    }
    onLoadStageProgress?.(0.75);

    sugarModel._joints?.forEach((joint) => {
      joint.apply(this);
    });
    onLoadStageProgress?.(0.9);

    sugarModel.loaded = true;

    if (onAfterLoad) await onAfterLoad(sugarModel, results);
    onLoadStageProgress?.(1);
    // Aynı sahneyle yeniden yüklendiyse (loadFromObject) yeni model eskisiyle aynıdır; sökülmez.
    if (oldModelToRemove !== this.model) oldModelToRemove?.removeFromParent();
    return sugarModel;
  }

  // NOTE: data tipi mecburen gevşek (any) tutuldu — alt sınıf SugarModelGroup
  // load'u { data, partConfig } sarmalayıcı objeyle override ediyor; parent burada
  // ModelData'ya daraltılırsa override Liskov uyumsuz oluyor. Refactor için TODO.
  async load(
    data: any,
    options?: {
      forceSync?: boolean;
      forceAgain?: boolean;
      /** Parçalı çarpışma kutuları. Yalnız room designer true geçer. */
      decomposeCollision?: boolean;
      onBeforeLoad?: (product: SugarModel) => void;
      onAfterLoad?: (
        sugarModel: SugarModel,
        results: any[],
      ) => void | Promise<void>;
      onProgress?: (progress: ProgressEvent) => void;
      onLoadStageProgress?: (progress: number) => void;
      /** Verilirse GLB indirilmez, bu sahne kullanılır (bkz. loadFromObject). */
      preparedObject?: Object3D;
    },
    materialDataList?: MaterialData[],
  ): Promise<SugarModel | undefined> {
    if (this.loaded && !options?.forceAgain) return this;
    if (options?.decomposeCollision != null) {
      this._decomposeCollision = options.decomposeCollision;
    }

    //this.model?.removeFromParent();
    this._invalidateCollisionBoundsForReload();
    this.loaded = false;
    this._defaultFeatureVisual = null;

    this.name =
      data.name || (data.id != null ? String(data.id) : "") || "SugarModel";

    this.userData["info"] = data;

    // Procedural structural items (columns / radiator) — no GLB fetch.
    if (data?.structuralKind) {
      const {
        applyStructuralSizeToData,
        createStructuralMesh,
        sizeFromStructuralData,
      } = await import("../shape/structural/StructuralCatalog");
      options?.onBeforeLoad?.(this);
      this._data = data;
      const size = applyStructuralSizeToData(data, sizeFromStructuralData(data));
      const mesh = createStructuralMesh(data.structuralKind, size);
      options?.onLoadStageProgress?.(0.5);
      this.model = mesh;
      this.loaded = true;
      options?.onLoadStageProgress?.(1);
      if (options?.onAfterLoad) await options.onAfterLoad(this, [mesh]);
      return this;
    }
    if (data.jointInfo && data.jointInfo.modelJoints) {
      this._joints = data.jointInfo.modelJoints?.map((joint: any) => {
        return new SugarModelJoint(joint);
      });
    } else if (Array.isArray(data.joints) && data.joints.length > 0) {
      this._joints = data.joints.map(
        (joint: any) => new SugarModelJoint(joint),
      );
      this.userData["info"].jointInfo = {
        id: data.id,
        excludes: data.excludes ?? [],
        modelJoints: data.joints,
      };
    }

    let materials: SugarMaterial[] = [];
    materialDataList = await ensureProductMaterialsCached(
      data,
      materialDataList,
    );
    materials = materialDataList.map((material) => createMaterial(material));
    let parts: SugarPart[] = [];
    if (data.parts) {
      parts = data.parts.map((part: any) => {
        return createPart(part);
      });
    }

    let partMaterialGroups;
    if (data.partMaterialGroups) {
      partMaterialGroups = data.partMaterialGroups.map(
        (partMaterialGroup: any) => {
          return new SugarPartGroup(partMaterialGroup, materials, parts);
        },
      );
    }

    //const sugarModel = new SugarModel(data);
    const sugarModel = this;
    sugarModel._data = data;
    sugarModel.materials = materials;
    sugarModel.parts = parts;
    sugarModel.partMaterialGroups = partMaterialGroups;
    //modelCache.set(data.id, sugarModel);
    //load begins

    const asyncFunctions = [];
    if (options?.preparedObject) {
      asyncFunctions.push(Promise.resolve(options.preparedObject));
    } else {
      const loader = new LoaderManager(new Sugar3DLoader());
      asyncFunctions.push(
        loader.load(data.gltf ?? "", { onProgress: options?.onProgress }, true),
      );
    }

    if (shouldLoadDefaultFeatureVisual(data, partMaterialGroups)) {
      const visual = createDefaultFeatureVisual(data, partMaterialGroups);
      if (visual) {
        this._defaultFeatureVisual = visual;
        asyncFunctions.push(
          forEachAsync(visual.parts, async (part) => {
            await part.load();
          }),
        );
        asyncFunctions.push(
          forEachAsync(visual.fabrics, async ({ fabric }) => {
            await fabric.load();
          }),
        );
      }
    }

    if (parts) {
      const partLoaders = forEachAsync(parts, async (part: any) => {
        await part.load();
      });
      asyncFunctions.push(partLoaders);
    }

    //
    if (partMaterialGroups) {
      const partLoaders = forEachAsync(
        partMaterialGroups,
        async (partMaterialGroupLoader: any) => {
          await partMaterialGroupLoader.load();
        },
      );
      asyncFunctions.push(partLoaders);
    }

    if (options?.onBeforeLoad) options?.onBeforeLoad(sugarModel);

    if (options?.forceSync) {
      await this._loadModel(
        sugarModel,
        Promise.all(asyncFunctions),
        options?.onAfterLoad,
        options?.onLoadStageProgress,
      );

      return sugarModel;
    } else {
      this._loadModel(
        sugarModel,
        Promise.all(asyncFunctions),
        options?.onAfterLoad,
        options?.onLoadStageProgress,
      );
      return sugarModel;
    }
  }

  /**
   * Hazırlanmış bir sahneyle (ör. editörde bellekteki çalışma geometrisi) yükler; GLB indirilmez.
   * Parça ve ürün partı uygulamaları `load` ile aynıdır. Aynı sahneyle tekrar çağrılabilir.
   * Sahne çağırana aittir: geometrisi önbellekle paylaşılmamalıdır, çünkü UV'ler yerinde ölçeklenir.
   */
  loadFromObject(
    data: ModelData,
    object: Object3D,
    materialDataList?: MaterialData[],
  ): Promise<SugarModel | undefined> {
    return this.load(
      data,
      { forceSync: true, forceAgain: true, preparedObject: object },
      materialDataList,
    );
  }

  /**
   * @param opts.keepTextures true ise texture'lar bırakılmaz. Ürün editörü modeli her değişiklikte
   *   yeniden kurar; materyal kopyaları (material.clone) texture'ları önbellekle ve yeni modelle
   *   paylaşır, onları atmak yeni modelin görüntüsünü bozar.
   */
  dispose(opts: { keepTextures?: boolean } = {}): void {
    untrackStateRoot(this);
    this._defaultFeatureVisual = null;
    this._clearObbDebugIfAny();
    if (this._model) {
      if (opts.keepTextures) disposeGeometriesAndMaterials(this._model);
      else disposeObject3DDeep(this._model);
      this._model.removeFromParent();
      this._model = undefined;
    }
    for (const b of this._bounds) {
      disposeObject3DDeep(b);
      b.removeFromParent();
    }
    this._bounds = [];
    this._bound = undefined;
    this._localObbs = [];
  }
}

/** Geometri ve materyalleri bırakır; materyallerin texture'larına dokunmaz (bkz. SugarModel.dispose). */
function disposeGeometriesAndMaterials(root: Object3D): void {
  root.traverse((o) => {
    if (!(o instanceof Mesh)) return;
    o.geometry.dispose();
    const materials = Array.isArray(o.material) ? o.material : [o.material];
    for (const material of materials) material.dispose();
  });
}
